/**
 * M7 S39 part 3 (spec §8.2): the Corporation screen's tetherstone ballot toggle. One row per measure (the ban, the
 * repeal) with the stance the corporation casts now (`story-link/finale.ts ballotStanceOf`: the toggle over the
 * default for the paths in force). Shown only with a corporation and after the first finale commit. A press cycles
 * for → against → abstain and writes through the story service (`setBallotStance`, which also sends the stance to
 * the worker). Labels are corpScreen.json `ballot`. UI events only.
 */
import { z } from 'zod'
import type { Career } from '../../career/types'
import { ballotStanceOf, setBallotStance } from '../../story-link/finale'
import type { StoryService } from '../../story-link/service'
import { BALLOT_STANCES, type BallotStance } from '../../story/types'
import { MEASURE_NAMES, type MeasureName } from '../../strategic/types'
import screenJson from './corpScreen.json'

const ballotScreenSchema = z.strictObject({
  heading: z.string().min(1),
  intro: z.string().min(1),
  measures: z.strictObject({ tetherstoneBan: z.string().min(1), tetherstoneRepeal: z.string().min(1) }),
  stances: z.strictObject({ for: z.string().min(1), against: z.string().min(1), abstain: z.string().min(1) }),
})
export type BallotScreenConfig = z.infer<typeof ballotScreenSchema>

let cfgCache: BallotScreenConfig | null = null
export function ballotScreenConfig(): BallotScreenConfig {
  cfgCache ??= ballotScreenSchema.parse(screenJson.ballot)
  return cfgCache
}

export interface BallotRow {
  measure: MeasureName
  label: string
  stance: BallotStance
  stanceLabel: string
}

/** The toggle's rows, or null when it is hidden (no corporation, no career, or before the first commit). */
export function ballotRows(career: Career | null, founded: boolean, cfg: BallotScreenConfig = ballotScreenConfig()): BallotRow[] | null {
  if (!founded || career === null) return null
  const rows: BallotRow[] = []
  for (const measure of MEASURE_NAMES) {
    const stance = ballotStanceOf(career, measure)
    if (stance === null) return null
    rows.push({ measure, label: cfg.measures[measure], stance, stanceLabel: cfg.stances[stance] })
  }
  return rows
}

/** The next stance in the cycle: for → against → abstain → for. */
export function nextStance(s: BallotStance): BallotStance {
  return BALLOT_STANCES[(BALLOT_STANCES.indexOf(s) + 1) % BALLOT_STANCES.length]
}

/** Cycles `measure`'s stance through the story service; false when nothing changed (hidden, no service, refused). */
export function cycleBallot(service: Pick<StoryService, 'apply'> | null, career: Career | null, measure: MeasureName): boolean {
  if (service === null || career === null) return false
  const now = ballotStanceOf(career, measure)
  if (now === null) return false
  const next = nextStance(now)
  let changed = false
  service.apply('story ballot', (c) => {
    const r = setBallotStance(c, measure, next)
    changed = r !== null
    return r ?? { career: c, commands: [], effects: [] }
  })
  return changed
}
